import { useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BOSSES, ITEMS, TITLES } from '@yuan-math/content';
import { levelForXp, type AvatarConfig, type LearnerProfile, type RpgClassId } from '@yuan-math/learning';
import {
  conditionLabel,
  conditionProgress,
  deriveTitle,
  earnedTitles,
  equippedItems,
  itemAvailability,
  ITEM_SLOTS,
  type ConditionNames,
  type ItemAvailability,
  type ItemDef,
  type ItemSlot,
} from '@yuan-math/world';
import { Avatar, AVATAR_OPTIONS, Button, Card, DEFAULT_AVATAR, type AvatarEquipment } from '@yuan-math/ui';
import { registry, useLearner } from '../../state/LearnerContext.tsx';
import { useRewards } from '../../state/RewardsContext.tsx';
import { classNameOf, RPG_CLASSES } from './classes.ts';

/**
 * Character creation — and re-creation, since a change of heart is allowed —
 * plus the wardrobe (docs/rpg.md §19, §31).
 *
 * Everything here is cosmetic and the page says so out loud: a class is a
 * costume (docs/rpg.md §10), so no card may hint at a gameplay difference,
 * and the whole ceremony is skippable — the adventure works with the default
 * avatar and no class at all. The wardrobe below it is the same claim about
 * equipment: an item is a drawing, and nothing in the platform reads one.
 *
 * The rule the wardrobe is written to is §0.6's — **say the honest thing**. A
 * locked item names its condition in full ("Master 10 Algebra skills") and
 * shows how far through it the student is; a shop item a student cannot afford
 * says how much short they are. Neither is ever replaced by a nudge, and no
 * tile hides what it wants behind a question mark.
 */

const GROUPS: readonly { key: keyof AvatarConfig; label: string }[] = [
  { key: 'skinTone', label: 'Skin tone' },
  { key: 'hair', label: 'Hair' },
  { key: 'hairColor', label: 'Hair colour' },
  { key: 'outfit', label: 'Outfit' },
  { key: 'accessory', label: 'Accessory' },
];

const OPTION_LABELS: Record<string, string> = {
  porcelain: 'Porcelain',
  fair: 'Fair',
  tan: 'Tan',
  brown: 'Brown',
  deep: 'Deep',
  short: 'Short',
  long: 'Long',
  curly: 'Curly',
  braids: 'Braids',
  buzz: 'Buzz cut',
  ponytail: 'Ponytail',
  black: 'Black',
  blonde: 'Blonde',
  red: 'Red',
  blue: 'Blue',
  tunic: 'Tunic',
  robe: 'Robe',
  armour: 'Armour',
  explorer: 'Explorer',
  'lab-coat': 'Lab coat',
  none: 'None',
  glasses: 'Glasses',
  cape: 'Cape',
  hat: 'Hat',
  backpack: 'Backpack',
};

/* -------------------------------------------------------------------------- */
/* The wardrobe                                                               */
/* -------------------------------------------------------------------------- */

const SLOT_LABELS: Record<ItemSlot, string> = {
  held: 'In your hands',
  cape: 'On your back',
  headgear: 'On your head',
};

/** Word, icon *and* colour for every state — never colour alone (CLAUDE.md). */
const AVAILABILITY: Record<ItemAvailability, { label: string; icon: string }> = {
  equipped: { label: 'Worn', icon: '✓' },
  owned: { label: 'Owned', icon: '🎒' },
  claimable: { label: 'Ready', icon: '⭐' },
  locked: { label: 'Not yet', icon: '🔒' },
};

const bossName = (id: string): string | undefined => BOSSES.find((boss) => boss.id === id)?.name;

/** What the avatar is wearing, as the drawing layers `Avatar` understands. */
function equipmentOf(profile: LearnerProfile): AvatarEquipment {
  const worn = equippedItems(ITEMS, profile);
  return { held: worn.held?.art, cape: worn.cape?.art, headgear: worn.headgear?.art };
}

/**
 * One item.
 *
 * A pressable tile in the same idiom as `class-card` — a coloured bottom edge
 * that sinks when pressed — and disabled only when there is genuinely nothing
 * to do, with the reason written underneath rather than left to the greying.
 */
function ItemTile({
  def,
  state,
  detail,
  action,
  onAct,
}: {
  def: ItemDef;
  state: ItemAvailability;
  detail: string;
  action: string | undefined;
  onAct: () => void;
}): JSX.Element {
  const { label, icon } = AVAILABILITY[state];
  return (
    <li>
      <button
        type="button"
        className={`item-tile item-tile--${state}`}
        aria-pressed={state === 'equipped'}
        disabled={action === undefined}
        onClick={onAct}
      >
        <span className="item-tile__emoji" aria-hidden="true">
          {def.emoji}
        </span>
        <span className="item-tile__name">{def.name}</span>
        <span className={`item-tile__state item-tile__state--${state}`}>
          <span aria-hidden="true">{icon}</span> {label}
        </span>
        <span className="item-tile__flavor">{def.flavor}</span>
        <span className="item-tile__detail">{detail}</span>
        {action ? <span className="item-tile__action">{action}</span> : null}
      </button>
    </li>
  );
}

function Wardrobe({ profile }: { profile: LearnerProfile }): JSX.Element {
  const { acquireItem, equipItem, unequipItem } = useLearner();
  const { celebrate } = useRewards();

  const gold = profile.rpg?.gold ?? 0;
  // Derived on every render, like mastery everywhere else: retention decays, so
  // an availability memoised on the profile would go quietly out of date.
  const now = Date.now();

  // The two lookups `conditionLabel` cannot do for itself: `packages/world`
  // holds neither the registry nor the boss list.
  const names: ConditionNames = { strand: (id) => registry.strand(id)?.title, boss: bossName };

  const act = (def: ItemDef, state: ItemAvailability): void => {
    if (state === 'equipped') {
      unequipItem(def.slot);
      return;
    }
    if (state === 'owned') {
      equipItem(def);
      return;
    }
    if (state !== 'claimable') return;

    acquireItem(def);
    /*
     * A toast, and nothing louder (CLAUDE.md — celebration is rationed).
     *
     * Confetti is spent on four things: a first-try correct answer, a finished
     * lesson, a main-chain quest claim and a first boss clear. Every one of them
     * is a piece of mathematics going right. Picking up a hat is not, however
     * pleasing it is — and a cosmetic that showered the screen would spend the
     * currency that makes finishing a kingdom's story feel like something. One
     * toast, keyed by item id so the queue de-duplicates it and no amount of
     * tapping stacks two.
     */
    celebrate({
      key: `item-${def.id}`,
      tone: 'achievement',
      icon: def.emoji,
      title: def.source.kind === 'shop' ? `Bought — ${def.name}` : `Earned — ${def.name}`,
      detail: def.source.kind === 'shop' ? `−${def.source.gold} gold` : 'Tap it again to wear it',
    });
  };

  return (
    <section className="wardrobe" aria-labelledby="wardrobe-heading">
      <div className="wardrobe__head">
        <h2 id="wardrobe-heading" className="class-picker__title">
          Wardrobe
        </h2>
        <p className="wardrobe__purse">
          <span aria-hidden="true">🪙</span> <strong>{gold}</strong> gold
        </p>
      </div>
      <p className="class-picker__note">
        Every piece here is a drawing and nothing else. Wearing one changes no question you are asked, no lesson you can
        open and no reward you can earn.
      </p>

      {ITEM_SLOTS.map((slot) => (
        <div key={slot} className="wardrobe__slot">
          <h3 className="wardrobe__slot-title">{SLOT_LABELS[slot]}</h3>
          <ul className="wardrobe__grid">
            {ITEMS.filter((def) => def.slot === slot).map((def) => {
              const state = itemAvailability(def, profile, registry.graph, now);
              return (
                <ItemTile
                  key={def.id}
                  def={def}
                  state={state}
                  detail={detailFor(def, state, gold, profile, now, names)}
                  action={actionFor(def, state)}
                  onAct={() => act(def, state)}
                />
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

/** The line under the flavour: what this item wants, or what it is. */
function detailFor(
  def: ItemDef,
  state: ItemAvailability,
  gold: number,
  profile: LearnerProfile,
  now: number,
  names: ConditionNames,
): string {
  if (state === 'equipped' || state === 'owned') {
    return def.source.kind === 'shop' ? 'Bought from the shop' : conditionLabel(def.source.condition, names);
  }
  if (def.source.kind === 'shop') {
    // The honest shortfall, in gold rather than in encouragement. A student can
    // work out how many quests that is; nothing here pretends it is nearly done.
    return state === 'locked'
      ? `${def.source.gold} gold — ${def.source.gold - gold} more to go`
      : `${def.source.gold} gold`;
  }
  const label = conditionLabel(def.source.condition, names);
  if (state === 'claimable') return label;
  const { done, total } = conditionProgress(def.source.condition, profile, registry.graph, now);
  // Counted, not rounded: "7 of 10" survives being true in a way "70%" does not.
  return total > 1 ? `${label} — ${Math.min(done, total)} of ${total}` : label;
}

/** The button's own word, or `undefined` when there is nothing to press it for. */
function actionFor(def: ItemDef, state: ItemAvailability): string | undefined {
  switch (state) {
    case 'equipped':
      return 'Take it off';
    case 'owned':
      return 'Wear it';
    case 'claimable':
      return def.source.kind === 'shop' ? `Buy for ${def.source.gold} gold` : 'Claim it';
    case 'locked':
      return undefined;
  }
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export function CharacterPage(): JSX.Element {
  const { profile, createCharacter } = useLearner();
  const navigate = useNavigate();

  const existing = profile?.rpg?.character;
  const [classId, setClassId] = useState<RpgClassId | undefined>(existing?.classId);
  const [avatar, setAvatar] = useState<AvatarConfig>(existing?.avatar ?? DEFAULT_AVATAR);

  if (!profile) return <div />;

  const setOption = (key: keyof AvatarConfig, value: string): void => {
    // `value` always comes out of `AVATAR_OPTIONS[key]`, so the pair is valid
    // even though TypeScript cannot correlate a computed key with its union.
    setAvatar((current) => ({ ...current, [key]: value }) as AvatarConfig);
  };

  const save = (): void => {
    if (!classId) return;
    createCharacter({ classId, avatar });
    navigate('/adventure');
  };

  const honorifics = earnedTitles(TITLES, profile, registry.graph, Date.now());

  return (
    <div className="page page--narrow">
      <header className="page-header">
        <h1 className="page__title">{existing ? 'Edit your character' : 'Create your character'}</h1>
        <p className="page__lead">
          Pick a class and a look. It is only a costume — every adventurer learns all mathematics, whatever they wear.
        </p>
      </header>

      <Card className="avatar-editor">
        <div className="avatar-editor__preview">
          {/* The subject of this page, so it is named rather than hidden — and
              named as the *preview* it is, because it changes under the
              student's hands as they press the options below. */}
          <Avatar
            config={avatar}
            classId={classId}
            equipment={equipmentOf(profile)}
            size={144}
            label={`Preview of your adventurer — ${classNameOf(classId)}, ${OPTION_LABELS[avatar.outfit] ?? avatar.outfit}, ${OPTION_LABELS[avatar.hair] ?? avatar.hair} hair`}
          />
          <p className="avatar-editor__preview-name">{profile.displayName}</p>
          {/*
           * The adventurer's title, and the honorifics behind it. Both are read
           * off the game — XP and deeds — and neither is a claim about
           * mathematics (docs/rpg.md §46), which is why the mastery figures on
           * the dashboard are the ones that carry that word.
           */}
          <p className="avatar-editor__title">{deriveTitle(levelForXp(profile.xp).level)}</p>
          {honorifics.length > 0 ? (
            <ul className="title-list">
              {honorifics.map((title) => (
                <li key={title.id} className="title-chip">
                  <span aria-hidden="true">🏅</span> {title.text}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="avatar-editor__controls">
          {GROUPS.map((group) => (
            <fieldset key={group.key} className="avatar-editor__group">
              <legend className="avatar-editor__legend">{group.label}</legend>
              <div className="avatar-editor__options">
                {AVATAR_OPTIONS[group.key].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`option-chip ${avatar[group.key] === value ? 'option-chip--chosen' : ''}`}
                    aria-pressed={avatar[group.key] === value}
                    onClick={() => setOption(group.key, value)}
                  >
                    {OPTION_LABELS[value]}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </Card>

      <section className="class-picker" aria-labelledby="class-picker-heading">
        <h2 id="class-picker-heading" className="class-picker__title">
          Choose a class
        </h2>
        <p className="class-picker__note">
          A class changes how you look, never what you can learn. Every adventurer learns all mathematics.
        </p>
        <div className="class-picker__grid">
          {RPG_CLASSES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`class-card ${classId === entry.id ? 'class-card--chosen' : ''}`}
              aria-pressed={classId === entry.id}
              onClick={() => setClassId(entry.id)}
            >
              <span className="class-card__emoji" aria-hidden="true">
                {entry.emoji}
              </span>
              <span className="class-card__name">{entry.name}</span>
              <span className="class-card__line">{entry.line}</span>
            </button>
          ))}
        </div>
      </section>

      <Wardrobe profile={profile} />

      <div className="button-row">
        <Button onClick={save} disabled={!classId}>
          {existing ? 'Save changes' : 'Start adventuring'}
        </Button>
        <Link to="/adventure" className="btn btn--ghost">
          {existing ? 'Cancel' : 'Skip for now'}
        </Link>
      </div>
      {!classId ? <p className="class-picker__hint">Pick a class to save — or skip, and adventure as you are.</p> : null}
    </div>
  );
}
